<template>
  <div class="voice-selector old-globals">
    <label v-if="showLabel" for="voice">Voice</label>
    <md-field>
      <md-select
        ref="select"
        id="voice"
        :value="voiceKey"
        :disabled="readonly"
        name="voice"
        class="voice-select"
        aria-label="Voice Selector"
        data-testid="voice-selector"
        @input="selectVoice"
        @md-opened="selectOpen"
        @md-closed="selectClosed"
      >
        <md-optgroup
          v-for="[aAvatarKey, avatarVoices] in avatarGroups"
          :key="aAvatarKey"
          :label="avatarLabel(aAvatarKey)"
          :aria-label="`${avatarLabel(aAvatarKey)} voice group`"
          data-testid="voice-group"
        >
          <md-option
            v-for="voice in avatarVoices"
            :key="voice.id"
            class="voice-option"
            :class="{ disabled: disabledVoiceKeys.includes(voice.id) }"
            :aria-label="`${voice.name.en} voice option`"
            data-testid="voice-option-list-item"
            :disabled="disabledVoiceKeys.includes(voice.id)"
            :value="voice.id"
          >
            {{ voice.name.en }}
            <span v-if="isOpen" class="description" :aria-label="`Voice description: ${voice.description.en}`" data-testid="voice-description">{{ voice.description.en }}</span>
          </md-option>
        </md-optgroup>
      </md-select>
      <md-button
        class="md-icon-button md-dense play-button"
        aria-label="Voice Play Button"
        data-testid="voice-play-button"
        :disabled="loadingAudio"
        @click="playSample"
      >
        <md-icon>
          {{ loadingAudio ? 'hourglass_top' : 'volume_up' }}
        </md-icon>
      </md-button>
    </md-field>
  </div>
</template>

<script>
import sortBy from 'lodash/sortBy';
import groupBy from 'lodash/groupBy';
import orderBy from 'lodash/orderBy';

import avatars from '~lib/common/config/avatars';
import servicesClient from '../../providers/servicesClient';

export default {
  name: 'voice-selector',
  props: {
    avatarKey: {
      type: String,
      default: null,
    },
    voiceKey: {
      type: String,
      default: null,
    },
    voices: {
      type: Array,
      default: () => [],
    },
    disabledVoiceKeys: {
      type: Array,
      default: () => [],
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    showLabel: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      isOpen: false,
      loadingAudio: false,
    }
  },
  computed: {
    avatarsByKey() {
      return Object.fromEntries(avatars.map(a => [a.key, a]));
    },
    voicesByAvatar() {
      return groupBy(this.sortedVoices, 'avatarKey');
    },
    avatarGroups() {
      return sortBy(Object.entries(this.voicesByAvatar), ([key]) => this.avatarLabel(key));
    },
    sortedVoices() {
      return orderBy(this.voices, 'ordinal', 'asc');
    }
  },
  methods: {
    avatarLabel(key) {
      const avatar = this.avatarsByKey[key];
      return avatar ? avatar.selectionName.en : key;
    },
    selectVoice(value) {
      this.$emit('update', value);
    },
    selectOpen() {
      this.isOpen = true;
      this.$nextTick(() => {
        if (this.$refs.select) {
          // Some hackery to get vue-material to scroll to the selected value.  Without
          // it, the first open of the dropdown won't put the value under the cursor.
          this.$refs.select.setOffsets();
        }
      });
    },
    selectClosed() {
      this.isOpen = false;
    },
    async playSample() {
      this.loadingAudio = true;
      servicesClient.post('generate-audio', {
        text: 'I\'m not feeling well',
        voiceId: this.voiceKey,
      }).then(response => {
        const audio = new Audio(response.data.s3Location);
        audio.play();
        this.loadingAudio = false;
      });
    }
  }
}
</script>

<style scoped>
.voice-selector {
  display: flex;
  flex-direction: column;
}

.voice-select {
  position: relative;
}

.md-optgroup >>> .md-subheader {
  font-size: 16px;
  font-weight: 400;
  padding-left: 24px;
}

.voice-option >>> .md-list-item-text {
  display: flex;
  flex-direction: row;
  position: relative;
  width: 440px;
}

.voice-option.disabled >>> .md-list-item-text,
.voice-option.disabled .description {
  color: var(--md-theme-default-disabled-on-background, rgba(0, 0, 0, 0.26));
}

.description {
  position: absolute;
  font-style: italic;
  left: 100px;
  top: 0px;
}

.icon-column {
  position: absolute;
  left: 210px;
  top: 0px;
}

.play-button {
  position: absolute;
  right: 30px;
  top: 0;
}

</style>
